import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@aions/ui-primitives';
import { generateReceiptPdf } from '../../lib/pdf-receipt';

/**
 * Desfecho dos uploads de anexo, que rodam DEPOIS de a denúncia estar gravada e
 * desta tela já estar no ar (ver IntakeWizard.handleSubmit). `falhos` guarda o NOME
 * dos arquivos que não subiram — a pessoa precisa saber qual arquivo guardar, e
 * perda silenciosa aqui seria indistinguível de sucesso.
 */
export interface AnexosOutcome {
  readonly fase: 'enviando' | 'concluido';
  readonly total: number;
  readonly falhos: ReadonlyArray<string>;
}

interface Props {
  protocolo: string;
  passphrase: string;
  /** caseId is optional for back-compat with existing tests; production wires it. */
  caseId?: string;
  onConsultar?: (() => void) | undefined;
  /** Ausente quando a denúncia não tinha anexo. */
  anexos?: AnexosOutcome | undefined;
}

export function Step5Confirmacao({
  protocolo,
  passphrase,
  caseId,
  onConsultar,
  anexos,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyToClipboard = useCallback(async (value: string, field: string) => {
    await navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  const downloadReceipt = useCallback(() => {
    // INT-06: PDF receipt is generated client-side and never crosses the wire.
    const blob = generateReceiptPdf(protocolo, passphrase, caseId ?? '');
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `comprovante-${protocolo}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }, [protocolo, passphrase, caseId]);

  return (
    <div className="fade-in">
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>✅</div>
        <h2>{t('step5.success_title')}</h2>
        <p style={{ color: 'var(--color-text-secondary)', marginTop: 8 }}>
          {t('step5.success_desc')}
        </p>
      </div>
      <Alert variant="critical" title={t('step5.warning_title')} style={{ marginBottom: 16 }}>
        {t('step5.warning_once')}
      </Alert>
      <div className="credential-box">
        <div className="credential-row">
          <div>
            <div className="credential-label">{t('step5.protocolo_label')}</div>
            <div className="credential-value" id="credential-protocolo">
              {protocolo}
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(protocolo, 'protocolo')}>
            {copiedField === 'protocolo' ? t('step5.copied') : t('step5.copy')}
          </Button>
        </div>
        <div className="credential-row">
          <div>
            <div className="credential-label">{t('step5.passphrase_label')}</div>
            <div className="credential-value" id="credential-passphrase">
              {passphrase}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => copyToClipboard(passphrase, 'passphrase')}
          >
            {copiedField === 'passphrase' ? t('step5.copied') : t('step5.copy')}
          </Button>
        </div>
      </div>
      {/* Anexos — só aparece se a denúncia tinha arquivo. O aviso de falha fica
          JUNTO das credenciais de propósito: é o momento em que a pessoa ainda tem
          o arquivo na mão e pode guardá-lo. */}
      {anexos && anexos.total > 0 && anexos.fase === 'enviando' && (
        <Alert variant="info" role="status" style={{ marginTop: 16 }} data-testid="anexos-enviando">
          {t('step5.anexos_enviando', { count: anexos.total })}
        </Alert>
      )}
      {anexos && anexos.fase === 'concluido' && anexos.falhos.length > 0 && (
        <Alert
          variant="warning"
          title={t('step5.anexos_falha_titulo', {
            falhos: anexos.falhos.length,
            total: anexos.total,
          })}
          style={{ marginTop: 16 }}
          data-testid="aviso-anexos-falhos"
        >
          <p style={{ margin: 0 }}>{t('step5.anexos_falha_corpo')}</p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            {anexos.falhos.map((nome) => (
              <li key={nome}>{nome}</li>
            ))}
          </ul>
          <p style={{ marginBottom: 0 }}>{t('step5.anexos_falha_saida')}</p>
        </Alert>
      )}
      <div className="wizard-actions" style={{ justifyContent: 'center', gap: 16 }}>
        <Button variant="ghost" onClick={downloadReceipt}>
          📄 {t('step5.download_pdf')}
        </Button>
        {onConsultar && (
          <Button variant="primary" onClick={onConsultar}>
            {t('step5.consultar_status')}
          </Button>
        )}
      </div>
    </div>
  );
}
